import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import BackendService from '../services/backendService';
import { DiscoveredTemplateStatus } from '../services/backendService/types';
import { GeneratorCoarseLicenseLevel } from '../services/TemplateService/Template.type';

const useCreateOrEditGenerator = (generatorId: string) => {
  const client = useQueryClient();
  const cacheKey = ['generators', generatorId];
  const { data: generator, isFetching: isFetchingGenerator } = useQuery(
    ['generators', generatorId],
    async () => {
      return BackendService.instance.ai.retrieveGenerator(generatorId);
    },
    {
      staleTime: 1000 * 60 * 5,
      retry: false,
      enabled: Boolean(generatorId),
    },
  );

  const { mutate: save, isLoading: isEditingOrCreatingGenerator } = useMutation(
    async ({
      generatorId,
      status,
      isPremiumGenerator,
      generatorPlan,
      coverImageId,
      backgroundImageId,
      coverImageAltText,
      learningStandards,
      durationInMins,
    }: {
      generatorId: string;
      status: DiscoveredTemplateStatus;
      isPremiumGenerator?: boolean;
      generatorPlan?: GeneratorCoarseLicenseLevel | null;
      coverImageId?: string;
      backgroundImageId?: string;
      coverImageAltText?: string;
      learningStandards?: string[];
      durationInMins?: number;
      onSuccess?: () => void;
    }) => {
      return BackendService.instance.ai.createOrEditGenerator({
        generatorId,
        status,
        isPremiumGenerator,
        coverImageId,
        backgroundImageId,
        coverImageAltText,
        generatorPlan,
        learningStandards,
        durationInMins,
      });
    },
    {
      onSuccess: (_, { onSuccess }) => {
        client.invalidateQueries(cacheKey);
        if (onSuccess) {
          onSuccess();
        }
      },
    },
  );

  const isGenerator = useMemo(() => {
    if (isFetchingGenerator) return undefined;
    return !!(generatorId && generator && isFetchingGenerator === false);
  }, [generator, generatorId, isFetchingGenerator]);

  return {
    generator,
    isGenerator,
    isFetchingGenerator,
    save,
    isEditingOrCreatingGenerator,
  };
};

export default useCreateOrEditGenerator;
